/**
 * Previews a colour on a product card when its swatch is hovered: the card
 * image, the colour name and the ring all move to that swatch.
 *
 * The Combined Listings app did this from inside its shadow root; the swatches
 * are the theme's own now, so it lives here. Its approach is followed closely:
 *
 *  - both card images are swapped, the resting one and the one the card shows
 *    on hover, so the pair stays the same colour
 *  - <source> elements inside a <picture> are cleared as well, since a source
 *    outranks the img's own src
 *  - the images are preloaded, so the swap does not blink on first hover
 *
 * Unlike the app, the preview is not undone when the pointer leaves: the card
 * keeps showing the last colour looked at until another swatch is hovered.
 *
 * Clicking a swatch previews it too rather than following its href. The
 * swatches are real links to the sibling products, so without this a tap on
 * mobile — where there is no hover — jumped straight to the PDP, leaving no way
 * to look at a colour before committing to it. The href stays in the markup for
 * crawlers, for the no-JS case, and for opening a colour in a new tab.
 *
 * Since the card now shows the previewed colour, the card's own links are moved
 * to that colour's product as well: otherwise the image would say White while
 * the title still led to Black.
 */
(function () {
  'use strict';

  var SWATCH = '.custom-rcl-swatches--card .rcl__swatch';
  var CARD = '.product-block';
  var MEDIA = 'img.theme-img, img.rimage__image';
  var SECOND_MEDIA = '.product-block__image--show-on-hover img';
  var LABEL = '.rcl__group-label-selected-value';
  var GRID = '.rcl__grid';
  // Every anchor on the card that points at the card's product. The image and
  // the title both carry .product-link; quick buy opens from the same href.
  var CARD_LINK = 'a.product-link, a.quickbuy-toggle';

  /**
   * @param {Element} scope
   * @param {string} selector
   * @returns {Element[]} the matching images plus any <source> siblings.
   */
  function mediaIn(scope, selector) {
    var images = Array.prototype.slice.call(scope.querySelectorAll(selector));

    images.forEach(function (image) {
      var picture = image.closest('picture');
      if (!picture) return;
      Array.prototype.forEach.call(picture.querySelectorAll('source'), function (source) {
        images.push(source);
      });
    });

    return images;
  }

  /**
   * @param {Element} node
   * @param {string} src - empty for a <source>, which only carries a srcset.
   */
  function swap(node, src) {
    // A srcset would otherwise outrank the src being set here.
    node.removeAttribute('srcset');
    if (src && node.tagName === 'IMG') node.setAttribute('src', src);
  }

  /** @param {HTMLElement} swatch */
  function preview(swatch) {
    var card = swatch.closest(CARD);
    if (!card) return;

    // The ring moves to this swatch and stays there, so the card keeps showing
    // the last colour looked at rather than snapping back.
    var grid = swatch.closest(GRID);
    if (grid) {
      grid.classList.add('is-previewing');
      grid.querySelectorAll('.rcl__swatch--preview').forEach(function (previous) {
        previous.classList.remove('rcl__swatch--preview');
      });
      swatch.classList.add('rcl__swatch--preview');
    }

    var main = swatch.dataset.swatchImage;
    var second = swatch.dataset.swatchImageSecond;
    if (main) mediaIn(card, MEDIA).forEach(function (node) { swap(node, main); });
    if (second) mediaIn(card, SECOND_MEDIA).forEach(function (node) { swap(node, second); });

    var label = card.querySelector(LABEL);
    if (label) label.textContent = swatch.dataset.optionValue || label.textContent;

    if (swatch.href) {
      card.querySelectorAll(CARD_LINK).forEach(function (link) {
        link.href = swatch.href;
      });
    }
  }

  /** Warms the cache so the first hover is not a blank frame. */
  function preload(scope) {
    (scope || document).querySelectorAll(SWATCH).forEach(function (swatch) {
      if (swatch.dataset.swatchPreloaded) return;
      swatch.dataset.swatchPreloaded = 'true';

      [swatch.dataset.swatchImage, swatch.dataset.swatchImageSecond].forEach(function (src) {
        if (!src) return;
        var image = new Image();
        image.src = src;
      });
    });
  }

  // Delegated: cards arrive with filtering, infinite scroll and quick buy.
  document.addEventListener('mouseover', function (evt) {
    var swatch = evt.target.closest && evt.target.closest(SWATCH);
    if (swatch) preview(swatch);
  });

  document.addEventListener('click', function (evt) {
    var swatch = evt.target.closest && evt.target.closest(SWATCH);
    if (!swatch) return;

    // A modified click is a deliberate "open this colour elsewhere", so it is
    // left to the browser.
    if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;

    evt.preventDefault();
    preview(swatch);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { preload(document); });
  } else {
    preload(document);
  }

  new MutationObserver(function () { preload(document); })
    .observe(document.documentElement, { childList: true, subtree: true });
})();
